import { Input, Select, InputNumber, Button, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

/**
 * A small, dependency-light "paper table" editor.
 *
 * columns: [{ key, label, width, type: 'text'|'select'|'number', options? }]
 * rows:    [{ sr_no, ...fields }]
 * onChange(nextRows)
 *
 * Used for every variable-length table on the physical Employee Clearance
 * Form (Dept Head items, IT items, Finance items, HR items) so the four
 * sections share one implementation instead of four bespoke tables.
 */
export default function EditableItemsTable({ columns, rows, onChange, addLabel = "Add row" }) {
  function updateCell(index, key, value) {
    const next = rows.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onChange(next);
  }

  function addRow() {
    const nextSrNo = rows.length ? Math.max(...rows.map((r) => r.sr_no || 0)) + 1 : 1;
    const blank = { sr_no: nextSrNo };
    columns.forEach((col) => {
      blank[col.key] = col.type === "number" ? 0 : col.defaultValue ?? "";
    });
    onChange([...rows, blank]);
  }

  function removeRow(index) {
    onChange(
      rows
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, sr_no: i + 1 }))
    );
  }

  return (
    <div className="paper-table">
      <table>
        <thead>
          <tr>
            <th className="paper-table-sr">Sr. No.</th>
            {columns.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                {col.label}
              </th>
            ))}
            <th className="paper-table-actions" aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className="paper-table-empty" colSpan={columns.length + 2}>
                No rows yet — add one below.
              </td>
            </tr>
          )}
          {rows.map((row, index) => (
            <tr key={row.id ?? `row-${index}`}>
              <td className="paper-table-sr mono">{row.sr_no ?? index + 1}</td>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.type === "select" ? (
                    <Select
                      size="small"
                      variant="borderless"
                      className="paper-table-select"
                      value={row[col.key]}
                      options={col.options}
                      onChange={(v) => updateCell(index, col.key, v)}
                      style={{ width: "100%" }}
                    />
                  ) : col.type === "number" ? (
                    <InputNumber
                      size="small"
                      variant="borderless"
                      min={0}
                      style={{ width: "100%" }}
                      value={row[col.key]}
                      onChange={(v) => updateCell(index, col.key, v ?? 0)}
                    />
                  ) : (
                    <Input
                      size="small"
                      variant="borderless"
                      value={row[col.key]}
                      placeholder={col.placeholder}
                      onChange={(e) => updateCell(index, col.key, e.target.value)}
                    />
                  )}
                </td>
              ))}
              <td className="paper-table-actions">
                <Tooltip title="Remove row">
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(index)}
                  />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addRow}
        className="paper-table-add"
      >
        {addLabel}
      </Button>
    </div>
  );
}